<script lang="ts" setup>
import { PhCheck, PhX } from '@phosphor-icons/vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

interface FortnoxAccount {
  id: string;
  name: string;
}

export interface FortnoxAccountViewProps {
  state: {
    status: 'connected' | 'disconnected';
    account: FortnoxAccount | null;
  };
}

export interface FortnoxAccountViewEmits {
  event: [payload: { type: 'connect' | 'disconnect' }];
}

const emit = defineEmits<FortnoxAccountViewEmits>();
defineProps<FortnoxAccountViewProps>();

const handleConnect = () => {
  emit('event', { type: 'connect' });
};

const handleDisconnect = () => {
  emit('event', { type: 'disconnect' });
};
</script>

<template>
  <div
    class="account-pill"
    :class="state.status === 'connected' ? 'is-success' : 'is-danger'"
  >
    <div class="icon-container">
      <span v-if="state.status === 'connected'" class="icon"><PhCheck /></span>
      <span v-else class="icon"><PhX /></span>
    </div>
    <div class="account-info">
      <p class="provider-name">Fortnox</p>
      <p v-if="state.account" class="account-name">{{ state.account.name }}</p>
    </div>
    <button
      v-if="state.status === 'connected'"
      class="button button-logout is-small"
      @click="handleDisconnect"
    >
      {{ t('logoutButton') }}
    </button>
    <button v-else class="button button-login is-small" @click="handleConnect">
      {{ t('loginButton') }}
    </button>
  </div>
</template>

<style scoped>
.account-pill {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.5rem;
  border-radius: 9999px;
  background-color: white;
  border: 2px solid;
  max-width: 400px;
  transition: all 0.2s ease;
}

.account-pill.is-success {
  border-color: #48c78e;
}

.account-pill.is-danger {
  border-color: #f14668;
}

.icon-container {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 2.5rem;
  height: 2.5rem;
  border-radius: 9999px;
  background-color: currentColor;
  color: white;
  font-weight: bold;
  font-size: 1.25rem;
}

.account-pill.is-success .icon-container {
  background-color: #48c78e;
}

.account-pill.is-danger .icon-container {
  background-color: #f14668;
}

.icon {
  line-height: 1;
}

.account-info {
  flex: 1;
  min-width: 0;
  margin-right: 1rem;
}

.provider-name {
  font-weight: 600;
  font-size: 0.875rem;
  margin: 0;
  line-height: 1.2;
}

.account-name {
  font-size: 0.75rem;
  color: #7a7a7a;
  margin: 0;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.button {
  flex-shrink: 0;
  border-radius: 9999px;
  font-size: 0.75rem;
  padding: 0.375rem 1.25rem;
  height: auto;
  font-weight: 600;
  transition: all 0.2s ease;
  border: none;
}

.button-login {
  background-color: var(--primary-color);
  color: white;
}

.button-login:hover {
  background-color: color-mix(in srgb, var(--primary-color) 90%, black);
}

.button-logout {
  background-color: transparent;
  color: #f14668;
  border: none;
  box-shadow: none;
  background-color: rgba(241, 70, 104, 0.1);
}

.button-logout:hover {
  background-color: rgba(241, 70, 104, 0.2);
}
</style>
